import React, { useEffect, useState, useCallback, FormEvent, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { guidelinesApi, Guideline } from "../../api";
import { celeryApi, CeleryTask } from "../../api/celery";
import GuidelinesPage from "./GuidelinesPage";
import CreateGuidelineDialog from "./CreateGuidelineDialog";

interface LanguageCount {
  language: string;
  count: number;
}

const GuidelinesContainer: React.FC = () => {
  const navigate = useNavigate();
  const [guidelines, setGuidelines] = useState<Guideline[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [chunkFilter, setChunkFilter] = useState<string>("all");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const pageSize = 20;

  // ── Embed task polling ────────────────────────────────────────────
  const [embedTasks, setEmbedTasks] = useState<CeleryTask[]>([]);
  const [embedTasksLoading, setEmbedTasksLoading] = useState(true);
  const [embedPanelExpanded, setEmbedPanelExpanded] = useState(true);
  const embedPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Tracks IDs of tasks that were active on the previous poll cycle.
  const prevActiveIdsRef = useRef<Set<string>>(new Set());
  // Stable ref so fetchEmbedTasks can always call the latest fetchGuidelines
  // without adding it to the dependency array (which would restart the interval).
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const fetchGuidelinesRef = useRef<() => void>(() => {});

  const ACTIVE_STATES = new Set(["STARTED", "PENDING", "RECEIVED", "RETRY"]);
  const EMBED_TASK_KEYWORDS = ["embed", "guideline", "vectorize", "ingest"];

  const fetchEmbedTasks = useCallback(async () => {
    try {
      // Fetch tasks that match known embedding task names
      const results = await Promise.allSettled(
        EMBED_TASK_KEYWORDS.map((kw) =>
          celeryApi.getTasks({ limit: 20, taskname: kw })
        )
      );
      const seen = new Set<string>();
      const merged: CeleryTask[] = [];
      for (const r of results) {
        if (r.status === "fulfilled") {
          for (const t of r.value.data.tasks) {
            if (!seen.has(t.id)) {
              seen.add(t.id);
              merged.push(t);
            }
          }
        }
      }
      // Keep active tasks + most-recent 5 completed ones
      const active = merged.filter((t) => ACTIVE_STATES.has((t.state || "").toUpperCase()));
      const recent = merged
        .filter((t) => !ACTIVE_STATES.has((t.state || "").toUpperCase()))
        .sort((a, b) => (b.received || 0) - (a.received || 0))
        .slice(0, 5);

      // If any previously-active task is no longer active, it just completed –
      // refresh the guidelines list so newly-ingested guidelines appear.
      const activeIds = new Set(active.map((t) => t.id));
      const justCompleted = Array.from(prevActiveIdsRef.current).some((id) => !activeIds.has(id));
      prevActiveIdsRef.current = activeIds;
      if (justCompleted) fetchGuidelinesRef.current();

      setEmbedTasks([...active, ...recent]);
    } catch {
      // silently ignore – non-critical
    } finally {
      setEmbedTasksLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Start polling; speed up (3 s) when tasks are active, slow down (15 s) otherwise
  useEffect(() => {
    fetchEmbedTasks();
    const hasActive = embedTasks.some((t) => ACTIVE_STATES.has((t.state || "").toUpperCase()));
    const interval = hasActive ? 3_000 : 15_000;
    embedPollRef.current = setInterval(fetchEmbedTasks, interval);
    return () => {
      if (embedPollRef.current) clearInterval(embedPollRef.current);
    };
  }, [fetchEmbedTasks, embedTasks.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGuidelines = useCallback(async () => {
    try {
      setLoading(true);
      const response = await guidelinesApi.list({
        limit: pageSize,
        offset: (page - 1) * pageSize,
        q: search || undefined,
      });
      setGuidelines(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch guidelines:", err);
      setGuidelines([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, search]);
  // Keep the ref in sync so fetchEmbedTasks always calls the latest version.
  useEffect(() => { fetchGuidelinesRef.current = fetchGuidelines; }, [fetchGuidelines]);

  useEffect(() => {
    fetchGuidelines();
  }, [fetchGuidelines]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchGuidelines();
  };

  const totalPages = Math.ceil(total / pageSize);

  const languageStats: Record<string, number> = {};
  guidelines.forEach((g) => {
    if (g.language) {
      languageStats[g.language] = (languageStats[g.language] || 0) + 1;
    }
  });
  const topLanguages: LanguageCount[] = Object.entries(languageStats)
    .map(([language, count]) => ({ language, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // Apply client-side filters for status and chunk ranges
  const applyChunkFilter = (g: Guideline) => {
    const count = (g.chunk_count || 0) as number;
    switch (chunkFilter) {
      case "0":
        return count === 0;
      case "1-10":
        return count >= 1 && count <= 10;
      case "11-50":
        return count >= 11 && count <= 50;
      case ">50":
        return count > 50;
      default:
        return true;
    }
  };

  const applyStatusFilter = (g: Guideline) => {
    if (statusFilter === "all") return true;
    // We don't have chunks detail here, so use available fields: chunk_count
    // and query the detail page for per-chunk embed status there. For list view,
    // interpret:
    // - to_embed: chunk_count > 0
    // - pending: chunk_count > 0 (best-effort)
    // - embedded: chunk_count === 0
    if (statusFilter === "to_embed") return (g.chunk_count || 0) > 0;
    if (statusFilter === "pending") return (g.chunk_count || 0) > 0;
    if (statusFilter === "embedded") return (g.chunk_count || 0) === 0;
    return true;
  };

  const filteredGuidelines = guidelines.filter((g) => applyChunkFilter(g) && applyStatusFilter(g));
  const filteredTotal = filteredGuidelines.length;

  const handleCreateSuccess = () => {
    setCreateDialogOpen(false);
    setPage(1);
    fetchGuidelines(); // Refresh the list
    // Give Celery/Flower ~1 s to register the new task, then refresh the
    // embed tasks panel so the running task shows up immediately.
    setTimeout(fetchEmbedTasks, 1000);
  };

  return (
    <>
      <GuidelinesPage
        guidelines={filteredGuidelines}
        loading={loading}
        search={search}
        onSearchChange={setSearch}
        onSearch={handleSearch}
        page={page}
        totalPages={totalPages}
        total={filteredTotal}
        statusFilter={statusFilter}
        chunkFilter={chunkFilter}
        onStatusChange={setStatusFilter}
        onChunkFilterChange={setChunkFilter}
        topLanguages={topLanguages}
        onNavigateDetail={(id) => navigate(`/guidelines/${id}`)}
        onPreviousPage={() => setPage((p) => p - 1)}
        onNextPage={() => setPage((p) => p + 1)}
        onCreateClick={() => setCreateDialogOpen(true)}
        embedTasks={embedTasks}
        embedTasksLoading={embedTasksLoading}
        embedPanelExpanded={embedPanelExpanded}
        onEmbedPanelToggle={() => setEmbedPanelExpanded((v) => !v)}
      />
      <CreateGuidelineDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </>
  );
};

export default GuidelinesContainer;
